import { useEffect, useState } from 'react'
import { formatDate } from 'date-fns'
import { useFormik } from 'formik'
import { enqueueSnackbar } from 'notistack'

import {
  PastEvent,
  PastEventsProductionState,
  PRODUCTION_REPORT_STATE,
  ProductionReportRequest,
  StreamEvent
} from '@img-arena/consoles-api-streaming'
import { Button, ListItemText, useTheme } from '@mui/material'
import { ProductionReport } from '@/components/forms/CreateProductionReportForm/types'
import { streamingRestApi } from '@/service/streamingApi'
import { onFieldChange } from '@/utils/forms/formik'

import { useActionMenuContext } from '../context/ActionMenuContext'
import { MenuItemLike } from '../index.types'

export const NO_ISSUES_LABEL = 'NO ISSUES' as const

type Props = MenuItemLike & {
  onAfterSubmit?: () => void
}

export const ActionReportNoIssues = <Data extends PastEvent>({
  overrideText,
  onAfterSubmit
}: Props) => {
  const theme = useTheme()
  const eventsApi = streamingRestApi.endpoints.events
  const [event, setEvent] = useState<StreamEvent | null>(null)

  const { row } = useActionMenuContext<Data>()

  useEffect(() => {
    const ac = new AbortController()

    eventsApi
      .getEvents({
        eventId: row.eventId,
        signal: ac.signal
      })
      .then(({ data }) => {
        setEvent(data)
        onFieldChange(formik, 'event', data)
      })

    return () => ac.abort()
  }, [row.eventId])

  const formik = useFormik<ProductionReport>({
    initialValues: {
      event,
      issues: [],
      startDate: formatDate(new Date(), 'dd/MM/yyyy hh:mm aa'),
      notes: [],
      state: PRODUCTION_REPORT_STATE.SUCCESS
    },

    onSubmit: async (data) => {
      if (!data.event) return

      try {
        await eventsApi.postProductionReport({
          eventId: row.eventId,
          requestBody: data as ProductionReportRequest
        })

        enqueueSnackbar({
          variant: 'success',
          message: 'Report submitted successfuly.'
        })

        onAfterSubmit?.()
      } catch {
        enqueueSnackbar({
          variant: 'error',
          message: 'Unable to submit the report.'
        })
      }
    }
  })

  const isSuccess = row.lastProductionReportState === PastEventsProductionState.SUCCESS
  const { backgroundColor: buttonBg, hoverBackgroundColor: buttonHoverBg } =
    theme.palette.productionReport.actionButton

  return (
    <Button
      variant="contained"
      sx={{
        backgroundColor: isSuccess ? theme.palette.success.main : buttonBg,
        '&:hover': {
          backgroundColor: isSuccess ? undefined : buttonHoverBg
        },
        mr: 1
      }}
      onClick={() => formik.submitForm()}
    >
      <ListItemText>{overrideText || NO_ISSUES_LABEL}</ListItemText>
    </Button>
  )
}
